import { Popconfirm, Space, Table } from 'antd'
import { deleteDeal, getAllDeals } from '../../controllers/deal_controller'
import RouteConstants from '../../routes_constants'
import { useEffect, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import {
    DeleteOutlined,
    PlusCircleOutlined,
    SearchOutlined,
} from '@ant-design/icons'
import { toast } from 'react-toastify'
import Loading from '../../molecules/loading'
import Column from 'antd/lib/table/Column'
import Text from 'antd/lib/typography/Text'
import MessageConstants from '../../message_constants'
import FilterDropdown from '../../molecules/filter_dropdown'
import { FilterConfirmProps } from 'antd/lib/table/interface'
import Title from 'antd/lib/typography/Title'
import { useGetUserInfo } from '../../controllers/authentication/clients/auth0'
import { MonitorSingleton } from '../../utils/monitor'
import { validateAndGetUserInfo } from '../../utils/validation_utils'
import {
    moneyNumberToString,
    percentageNumberToString,
} from '../../utils/data_mapping_utils'

type DataSourceObject = {
    key: string
    name: string
    dealType: string
    dealValue: string
    commission: string
    assetType: string
    assetValue: string
}

export default function ViewAllDeals() {
    const getUserInfo = useGetUserInfo()
    const [tableData, setTableData] = useState<DataSourceObject[]>([])
    const [doneLoading, setDoneLoading] = useState<boolean>(false)
    const [selectedRows, setSelectedRows] = useState<
        DataSourceObject[] | undefined
    >(undefined)
    const [_searchText, setSearchText] = useState<string | undefined>()
    const [_searchedColumn, setSearchedColumn] = useState<string | undefined>()
    const [confirmDeleting, setConfirmDeleting] = useState(false)
    const navigate = useNavigate()

    const loadUserData = async () => {
        try {
            const userInfo = await validateAndGetUserInfo(getUserInfo, navigate)
            if (!userInfo) {
                return
            }

            const deals = await getAllDeals(userInfo.id)

            setTableData(
                deals.map((deal) => {
                    return {
                        key: deal.entityId,
                        name: deal.name,
                        dealType: deal.dealType ?? '',
                        dealValue: moneyNumberToString(deal.dealValue),
                        commission: percentageNumberToString(deal.commission),
                        assetType: deal.assetType ?? '',
                        assetValue: moneyNumberToString(deal.assetValue),
                    }
                })
            )
        } catch (err) {
            MonitorSingleton.sendException(err)
            toast(MessageConstants.UNABLE_TO_LOAD_DEALS, {
                type: 'error',
            })
        } finally {
            setDoneLoading(true)
        }
    }

    useEffect(() => {
        loadUserData()
    }, [])

    let onSelectedRowChange = (
        selectedRowKeys: React.Key[],
        selectedRows: DataSourceObject[]
    ) => {
        if (selectedRows.length === 0) {
            setSelectedRows(undefined)
        } else {
            setSelectedRows(selectedRows)
        }
    }

    let handleSearch = (
        selectedKeys: React.Key[],
        confirm: (param?: FilterConfirmProps | undefined) => void,
        dataIndex: string
    ) => {
        confirm()
        setSearchText(selectedKeys[0]?.toString())
        setSearchedColumn(dataIndex)
    }

    let handleReset = (clearFilters: (() => void) | undefined) => {
        if (clearFilters) {
            clearFilters()
        }
        setSearchText('')
    }

    let filteredIconFunction = (filtered: boolean) => (
        <SearchOutlined
            style={{
                color: filtered ? '#1890ff' : undefined,
            }}
        />
    )

    let handleDeleteClick = async () => {
        setConfirmDeleting(true)
        try {
            const userInfo = await validateAndGetUserInfo(getUserInfo, navigate)

            if (!userInfo || !selectedRows || selectedRows.length <= 0) {
                return
            }
            await Promise.all(
                selectedRows.map((item: DataSourceObject) => {
                    return deleteDeal(userInfo?.id, item.key)
                })
            )
            toast('Deals deleted', {
                type: 'success',
            })
            setSelectedRows(undefined)
            loadUserData()
        } catch (e) {
            toast('Error occurred when trying to delete deal', {
                type: 'error',
            })
        } finally {
            setConfirmDeleting(false)
        }
    }

    return (
        <div>
            <div
                style={{
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    alignItems: 'center',
                }}
            >
                <Title level={3}>Deals</Title>
            </div>
            <div
                style={{
                    display: 'flex',
                    flexDirection: 'row-reverse',
                    alignItems: 'center',
                    margin: '15px',
                }}
            >
                <Link to={RouteConstants.ADD_DEAL}>
                    <PlusCircleOutlined
                        style={{ fontSize: '25px', paddingLeft: '10px' }}
                    />
                </Link>
                {selectedRows && selectedRows.length > 0 && (
                    <Popconfirm
                        title={
                            'Delete selected deal' +
                            (selectedRows.length > 1 ? 's' : '') +
                            '?'
                        }
                        okText={'Delete'}
                        okButtonProps={{ loading: confirmDeleting }}
                        okType="danger"
                        cancelButtonProps={{ disabled: confirmDeleting }}
                        onConfirm={handleDeleteClick}
                    >
                        <DeleteOutlined
                            style={{
                                fontSize: '25px',
                                paddingLeft: '10px',
                                color: 'red',
                            }}
                        />
                    </Popconfirm>
                )}
            </div>
            {
                !doneLoading && <Loading />
                /* TODO: Consider not having a separate loading (data will just naturally be empty?)  --> Maybe use a default (... data type?) */
            }
            {doneLoading && (
                <Table
                    showHeader
                    rowSelection={{
                        preserveSelectedRowKeys: false,
                        type: 'checkbox',
                        onChange: onSelectedRowChange,
                    }}
                    dataSource={tableData}
                >
                    <Column
                        title="Name"
                        dataIndex="name"
                        key="name"
                        sorter={(a: DataSourceObject, b: DataSourceObject) =>
                            a.name.localeCompare(b.name)
                        }
                        width="20%"
                        ellipsis
                        filterDropdown={({
                            setSelectedKeys,
                            selectedKeys,
                            confirm,
                            clearFilters,
                        }) => {
                            return (
                                <FilterDropdown
                                    setSelectedKeys={setSelectedKeys}
                                    selectedKeys={selectedKeys}
                                    confirm={confirm}
                                    clearFilters={clearFilters}
                                    dataColumnLabel="name"
                                    handleReset={handleReset}
                                    handleSearch={handleSearch}
                                />
                            )
                        }}
                        onFilter={(
                            value: string | number | boolean,
                            record: any
                        ) =>
                            record['name']
                                ? record['name']
                                      .toString()
                                      .toLowerCase()
                                      .includes(value.toString().toLowerCase())
                                : ''
                        }
                        filterIcon={filteredIconFunction}
                    />
                    <Column
                        title="Deal Type"
                        dataIndex="dealType"
                        key="dealType"
                        sorter={(a: DataSourceObject, b: DataSourceObject) =>
                            a.dealType.localeCompare(b.dealType)
                        }
                        width="10%"
                        ellipsis
                        filterDropdown={({
                            setSelectedKeys,
                            selectedKeys,
                            confirm,
                            clearFilters,
                        }) => {
                            return (
                                <FilterDropdown
                                    setSelectedKeys={setSelectedKeys}
                                    selectedKeys={selectedKeys}
                                    confirm={confirm}
                                    clearFilters={clearFilters}
                                    dataColumnLabel="dealType"
                                    handleReset={handleReset}
                                    handleSearch={handleSearch}
                                />
                            )
                        }}
                        onFilter={(
                            value: string | number | boolean,
                            record: any
                        ) =>
                            record['dealType']
                                ? record['dealType']
                                      .toString()
                                      .toLowerCase()
                                      .includes(value.toString().toLowerCase())
                                : ''
                        }
                        filterIcon={filteredIconFunction}
                    />
                    <Column
                        title="Deal Value"
                        dataIndex="dealValue"
                        key="dealValue"
                        sorter={(a: DataSourceObject, b: DataSourceObject) =>
                            a.dealValue.localeCompare(b.dealValue)
                        }
                        width="20%"
                        ellipsis
                        filterDropdown={({
                            setSelectedKeys,
                            selectedKeys,
                            confirm,
                            clearFilters,
                        }) => {
                            return (
                                <FilterDropdown
                                    setSelectedKeys={setSelectedKeys}
                                    selectedKeys={selectedKeys}
                                    confirm={confirm}
                                    clearFilters={clearFilters}
                                    dataColumnLabel="dealValue"
                                    handleReset={handleReset}
                                    handleSearch={handleSearch}
                                />
                            )
                        }}
                        onFilter={(
                            value: string | number | boolean,
                            record: any
                        ) =>
                            record['dealValue']
                                ? record['dealValue']
                                      .toString()
                                      .toLowerCase()
                                      .includes(value.toString().toLowerCase())
                                : ''
                        }
                        filterIcon={filteredIconFunction}
                    />
                    <Column
                        title="Commission"
                        dataIndex="commission"
                        key="commission"
                        sorter={(a: DataSourceObject, b: DataSourceObject) =>
                            a.commission.localeCompare(b.commission)
                        }
                        width="15%"
                        ellipsis
                        filterDropdown={({
                            setSelectedKeys,
                            selectedKeys,
                            confirm,
                            clearFilters,
                        }) => {
                            return (
                                <FilterDropdown
                                    setSelectedKeys={setSelectedKeys}
                                    selectedKeys={selectedKeys}
                                    confirm={confirm}
                                    clearFilters={clearFilters}
                                    dataColumnLabel="commission"
                                    handleReset={handleReset}
                                    handleSearch={handleSearch}
                                />
                            )
                        }}
                        onFilter={(
                            value: string | number | boolean,
                            record: any
                        ) =>
                            record['commission']
                                ? record['commission']
                                      .toString()
                                      .toLowerCase()
                                      .includes(value.toString().toLowerCase())
                                : ''
                        }
                        filterIcon={filteredIconFunction}
                    />
                    <Column
                        title="Asset Type"
                        dataIndex="assetType"
                        key="assetType"
                        sorter={(a: DataSourceObject, b: DataSourceObject) =>
                            a.assetType.localeCompare(b.assetType)
                        }
                        width="25%"
                        ellipsis
                        filterDropdown={({
                            setSelectedKeys,
                            selectedKeys,
                            confirm,
                            clearFilters,
                        }) => {
                            return (
                                <FilterDropdown
                                    setSelectedKeys={setSelectedKeys}
                                    selectedKeys={selectedKeys}
                                    confirm={confirm}
                                    clearFilters={clearFilters}
                                    dataColumnLabel="assetType"
                                    handleReset={handleReset}
                                    handleSearch={handleSearch}
                                />
                            )
                        }}
                        onFilter={(
                            value: string | number | boolean,
                            record: any
                        ) =>
                            record['assetType']
                                ? record['assetType']
                                      .toString()
                                      .toLowerCase()
                                      .includes(value.toString().toLowerCase())
                                : ''
                        }
                        filterIcon={filteredIconFunction}
                    />
                    <Column
                        title="Asset Value"
                        dataIndex="assetValue"
                        key="assetValue"
                        sorter={(a: DataSourceObject, b: DataSourceObject) =>
                            a.assetValue.localeCompare(b.assetValue)
                        }
                        width="20%"
                        ellipsis
                        filterDropdown={({
                            setSelectedKeys,
                            selectedKeys,
                            confirm,
                            clearFilters,
                        }) => {
                            return (
                                <FilterDropdown
                                    setSelectedKeys={setSelectedKeys}
                                    selectedKeys={selectedKeys}
                                    confirm={confirm}
                                    clearFilters={clearFilters}
                                    dataColumnLabel="assetValue"
                                    handleReset={handleReset}
                                    handleSearch={handleSearch}
                                />
                            )
                        }}
                        onFilter={(
                            value: string | number | boolean,
                            record: any
                        ) =>
                            record['assetValue']
                                ? record['assetValue']
                                      .toString()
                                      .toLowerCase()
                                      .includes(value.toString().toLowerCase())
                                : ''
                        }
                        filterIcon={filteredIconFunction}
                    />
                    <Column
                        title="Action"
                        key="action"
                        width="15%"
                        render={(text, record: any) => (
                            <Space size="middle">
                                <Text>
                                    <Link
                                        to={RouteConstants.viewDealRoute(
                                            record.key
                                        )}
                                    >
                                        View Details
                                    </Link>
                                </Text>
                            </Space>
                        )}
                    />
                </Table>
            )}
        </div>
    )
}
